// Přehled pošty – přijato / odesláno za období s grafem (postup 76): Správa serveru (po firmách) i Správa firmy
// (po účtech). Lišta: Období ▾, Ukazatel ▾ (počet zpráv / objem dat), Obnovit; obsah: souhrn, sloupcový graf, tabulka.
import { registerComponent } from '@core/views/factory';
import { action, ApiError } from '@core/api';
import { h } from '@core/dom';
import { t, tk, fmtSize } from '@core/i18n';
import { simpleBar } from '@core/components/chrome';
import { barChart } from '@core/components/chart';
import { dialog } from '@core/components/dialog';
import { toast } from '@core/components/toast';
import type { MenuItem } from '@core/components/menu';

interface Sum { in_n: number; in_b: number; out_n: number; out_rcpt: number; out_b: number; failed: number }
interface Res { from: string; to: string; months: boolean; buckets: Record<string, Sum>; rows: (Sum & { name: string })[] }

registerComponent('posta-stats', ({ desc, win, wm }) => {
  const p = desc.payload as { server: boolean; today: string };
  let period = '30d';
  let measure: 'count' | 'bytes' = 'count';
  let custom: { from: string; to: string } | null = null;
  const tiles = h('div', { class: 'stats-tiles' });
  const chartBox = h('div', { class: 'stats-chart' });
  const table = h('div', { class: 'stats-table' });
  const el = h('div', { class: 'view stats-view chrome-bar' }, tiles, chartBox, table);
  const day = (d: string, back: number): string => { const x = new Date(d + 'T12:00:00'); x.setDate(x.getDate() - back); return x.toISOString().slice(0, 10); };
  const range = (): { from: string; to: string } => {
    if (period === 'custom' && custom) return custom;
    const back = { '7d': 6, '30d': 29, '90d': 89, '12m': 364 }[period] ?? 29;
    return { from: day(p.today, back), to: p.today };
  };
  const periods: [string, string][] = [['7d', 'posta.stats.p7'], ['30d', 'posta.stats.p30'], ['90d', 'posta.stats.p90'], ['12m', 'posta.stats.p12m']];
  const num = (v: number) => v.toLocaleString('cs-CZ');
  const items = () => [
    { id: 'period', label: t('posta.stats.period') + ': ' + (period === 'custom' && custom ? `${custom.from} – ${custom.to}` : t(periods.find((x) => x[0] === period)?.[1] ?? 'posta.stats.p30')), icon: 'calendar', place: 'primary' as const, menu: [
      ...periods.map(([v, l]): MenuItem => ({ label: t(l), checked: period === v, onSelect: () => { period = v; void load(); } })),
      { label: t('posta.stats.custom'), checked: period === 'custom', onSelect: () => void pickCustom() },
    ] },
    { id: 'measure', label: measure === 'count' ? t('posta.stats.count') : t('posta.stats.bytes'), icon: 'sort', place: 'action' as const, menu: [
      { label: t('posta.stats.count'), checked: measure === 'count', onSelect: () => { measure = 'count'; void load(); } },
      { label: t('posta.stats.bytes'), checked: measure === 'bytes', onSelect: () => { measure = 'bytes'; void load(); } },
    ] },
    { id: 'refresh', label: t('core.cmd.refresh'), icon: 'refresh', place: 'more' as const, shortcut: 'F5', onClick: () => void load() },
  ];
  const sb = simpleBar(win, wm, items);
  async function pickCustom(): Promise<void> {
    const r0 = range();
    const f = h('input', { class: 'input', type: 'date', value: r0.from }) as HTMLInputElement;
    const tt = h('input', { class: 'input', type: 'date', value: r0.to }) as HTMLInputElement;
    const res = await dialog({ title: t('posta.stats.custom'), message: h('div', { class: 'stats-custom' }, h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('posta.stats.from')), f), h('label', { class: 'field' }, h('span', { class: 'field-label' }, t('posta.stats.to')), tt)), buttons: [{ id: 'cancel', label: t('core.ui.cancel') }, { id: 'ok', label: t('core.ui.ok'), primary: true }] });
    if (res.id !== 'ok' || !f.value || !tt.value) return;
    custom = { from: f.value, to: tt.value };
    period = 'custom';
    void load();
  }
  async function load(): Promise<void> {
    sb.refresh();
    const r0 = range();
    try {
      const r = await action<Res>(desc.app, desc.window, 'stats', desc.params, r0);
      render(r);
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }
  function render(r: Res): void {
    const keys = Object.keys(r.buckets);
    const total = keys.reduce((s, k) => { const b = r.buckets[k] as Sum; return { in_n: s.in_n + b.in_n, in_b: s.in_b + b.in_b, out_n: s.out_n + b.out_n, out_b: s.out_b + b.out_b, failed: s.failed + b.failed }; }, { in_n: 0, in_b: 0, out_n: 0, out_b: 0, failed: 0 });
    const tile = (label: string, main: string, sub: string, cls = '') => h('div', { class: 'stats-tile ' + cls }, h('div', { class: 'stats-tile-label' }, label), h('div', { class: 'stats-tile-main' }, main), h('div', { class: 'stats-tile-sub' }, sub));
    tiles.replaceChildren(
      tile(t('posta.stats.received'), num(total.in_n), fmtSize(total.in_b), 'in'),
      tile(t('posta.stats.sent'), num(total.out_n), fmtSize(total.out_b), 'out'),
      tile(t('posta.stats.failed'), num(total.failed), t('posta.stats.failed_hint'), total.failed ? 'bad' : ''),
    );
    last = r;
    drawChart();
    const th = (s: string, cls = '') => h('th', { class: cls }, s);
    const td = (s: string, cls = 'right') => h('td', { class: cls }, s);
    table.replaceChildren(r.rows.length
      ? h('table', { class: 't stats-t' },
        h('thead', null, h('tr', null, th(p.server ? t('posta.stats.customer') : t('posta.stats.account')), th(t('posta.stats.received'), 'right'), th(t('posta.stats.data_in'), 'right'), th(t('posta.stats.sent'), 'right'), th(t('posta.stats.rcpt'), 'right'), th(t('posta.stats.data_out'), 'right'), th(t('posta.stats.failed'), 'right'))),
        h('tbody', null, r.rows.map((x) => h('tr', null, td(x.name, ''), td(num(x.in_n)), td(fmtSize(x.in_b)), td(num(x.out_n)), td(num(x.out_rcpt)), td(fmtSize(x.out_b)), td(num(x.failed), x.failed ? 'right bad' : 'right')))))
      : h('p', { class: 'muted' }, t('posta.stats.empty')));
  }
  // graf na skutečnou šířku: překreslit při změně velikosti okna (maximalizace, tažení)
  let last: Res | null = null;
  let lastW = 0;
  new ResizeObserver(() => { if (last && Math.abs(chartBox.clientWidth - lastW) > 8) drawChart(); }).observe(chartBox);
  function drawChart(): void {
    const r = last;
    if (!r) return;
    lastW = chartBox.clientWidth;
    const keys = Object.keys(r.buckets);
    const label = (k: string) => (r.months ? `${k.slice(5, 7)}/${k.slice(0, 4)}` : `${Number(k.slice(8, 10))}. ${Number(k.slice(5, 7))}.`);
    const pick = (b: Sum, dir: 'in' | 'out') => (measure === 'count' ? (dir === 'in' ? b.in_n : b.out_n) : dir === 'in' ? b.in_b : b.out_b);
    const css = getComputedStyle(document.documentElement);
    chartBox.replaceChildren(barChart(keys.map(label), [
      { name: t('posta.stats.received'), color: css.getPropertyValue('--chart-in').trim() || '#1a73e8', values: keys.map((k) => pick(r.buckets[k] as Sum, 'in')) },
      { name: t('posta.stats.sent'), color: css.getPropertyValue('--chart-out').trim() || '#9b0030', values: keys.map((k) => pick(r.buckets[k] as Sum, 'out')) },
    ], measure === 'count' ? (v) => num(Math.round(v)) : (v) => fmtSize(v), 240, Math.max(320, chartBox.clientWidth - 4)));
  }
  void load();
  return { el, refresh: () => void load(), setMode: () => sb.refresh() };
});
